import { normalizeBuiltInCommonMeasureId } from "../../shared/commonMeasures";

const URL_SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:/i;
const EXPLICIT_DIETARY_SUITABILITY_OPTIONS = new Set([
  "none",
  "vegetarian",
  "vegan",
]);

export const normalizeDecimalInput = (value) =>
  String(value ?? "")
    .replace(",", ".")
    .replace(/[^\d.]/g, "");

export const normalizeWebsiteUrl = (value) => {
  const trimmedValue = String(value ?? "").trim();
  if (!trimmedValue) {
    return "";
  }

  const candidate = URL_SCHEME_PATTERN.test(trimmedValue)
    ? trimmedValue
    : `https://${trimmedValue}`;

  try {
    const parsedUrl = new URL(candidate);
    const isSupportedProtocol =
      parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
    const hasValidHostname =
      parsedUrl.hostname === "localhost" || parsedUrl.hostname.includes(".");

    if (!isSupportedProtocol || !hasValidHostname) {
      return "";
    }

    return candidate;
  } catch {
    return "";
  }
};

export const parseNumberInput = (value) => {
  const normalizedValue = normalizeDecimalInput(value);
  if (normalizedValue.trim() === "") {
    return null;
  }

  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export function formatIntegerInput(value) {
  const parsedValue = parseNumberInput(value);
  if (parsedValue == null) {
    return "";
  }

  return String(Math.round(parsedValue));
}

export function formatSingleDecimalInput(value) {
  const parsedValue = parseNumberInput(value);
  if (parsedValue == null) {
    return "";
  }

  return Number.isInteger(parsedValue)
    ? String(parsedValue)
    : parsedValue.toFixed(1);
}

export function formatMicronutrientInput(value) {
  const parsedValue = parseNumberInput(value);
  if (parsedValue == null) {
    return "";
  }

  const absoluteValue = Math.abs(parsedValue);

  if (absoluteValue === 0) {
    return "0";
  }

  if (absoluteValue < 1) {
    return parsedValue.toFixed(2);
  }

  if (absoluteValue < 10) {
    return parsedValue.toFixed(1);
  }

  return Number.isInteger(parsedValue)
    ? String(parsedValue)
    : parsedValue.toFixed(1);
}

function parsePositiveNumberInput(value) {
  const parsedValue = parseNumberInput(value);
  return parsedValue != null && parsedValue > 0 ? parsedValue : null;
}

export function getInitialFoodQuantityInputValue({
  quantity = null,
  quantityReferenceG = null,
  reference_quantity_g = null,
  quantityUnitKey = "grams",
  selected_measure_id = null,
  selectedMeasureId = null,
  commonMeasures = [],
} = {}) {
  const resolvedQuantityUnitKey =
    String(
      quantityUnitKey ?? selected_measure_id ?? selectedMeasureId ?? "grams",
    ).trim() || "grams";
  const normalizedQuantityUnitKey =
    resolvedQuantityUnitKey === "grams"
      ? "grams"
      : normalizeBuiltInCommonMeasureId(resolvedQuantityUnitKey);
  const referenceQuantityInGrams =
    parsePositiveNumberInput(quantityReferenceG) ??
    parsePositiveNumberInput(reference_quantity_g);

  if (normalizedQuantityUnitKey !== "grams") {
    const selectedMeasure = (Array.isArray(commonMeasures) ? commonMeasures : []).find(
      (measure) =>
        normalizeBuiltInCommonMeasureId(
          measure?.measureKey ?? measure?.id,
        ) === normalizedQuantityUnitKey,
    );
    const gramsPerMeasure = parsePositiveNumberInput(selectedMeasure?.grams);

    if (referenceQuantityInGrams != null && gramsPerMeasure != null) {
      return formatSingleDecimalInput(referenceQuantityInGrams / gramsPerMeasure);
    }
  }

  return formatSingleDecimalInput(
    referenceQuantityInGrams ?? parsePositiveNumberInput(quantity) ?? 100,
  );
}

export function getResolvedFoodDraftValues(form = {}) {
  const quantityValue = parseNumberInput(form.quantity);
  const energyValue = parseNumberInput(form.energy);
  const proteinValue = parseNumberInput(form.protein);
  const carbsValue = parseNumberInput(form.carbs);
  const fatsValue = parseNumberInput(form.fats);
  const fiberValue = parseNumberInput(form.fiber);
  const selectedQuantityMeasure =
    form.quantityUnitKey === "grams"
      ? null
      : (form.commonMeasures?.find(
          (measure) => measure.measureKey === form.quantityUnitKey,
        ) ?? null);
  const selectedQuantityMeasureGrams = parseNumberInput(
    selectedQuantityMeasure?.grams,
  );
  const resolvedQuantityInGrams =
    form.quantityUnitKey === "grams"
      ? quantityValue
      : quantityValue != null &&
          quantityValue > 0 &&
          selectedQuantityMeasureGrams != null &&
          selectedQuantityMeasureGrams > 0
        ? quantityValue * selectedQuantityMeasureGrams
        : null;
  const normalizedWebsite = normalizeWebsiteUrl(form.website);
  const hasWebsiteValue = String(form.website ?? "").trim().length > 0;
  const isWebsiteValid = !hasWebsiteValue || normalizedWebsite.length > 0;
  const canSave = String(form.name ?? "").trim().length > 0 && isWebsiteValid;

  return {
    quantityValue,
    energyValue,
    proteinValue,
    carbsValue,
    fatsValue,
    fiberValue,
    resolvedQuantityInGrams,
    normalizedWebsite,
    hasWebsiteValue,
    isWebsiteValid,
    canSave,
  };
}

export function getAddFoodModalSubmitIssues(form = {}) {
  const resolvedDraft = getResolvedFoodDraftValues(form);
  const hasExplicitDietarySuitability = EXPLICIT_DIETARY_SUITABILITY_OPTIONS.has(
    String(form.dietarySuitability ?? "").trim().toLowerCase(),
  );
  const hasIncompleteCommonMeasure = (Array.isArray(form.commonMeasures)
    ? form.commonMeasures
    : []
  ).some((measure) => {
    const gramsValue = parseNumberInput(measure?.grams);
    return gramsValue == null || gramsValue <= 0;
  });

  const issues = {
    name: String(form.name ?? "").trim().length === 0,
    quantity:
      resolvedDraft.quantityValue == null || resolvedDraft.quantityValue <= 0,
    allergens: form.allergenDataComplete !== true,
    preferences: !hasExplicitDietarySuitability,
    commonMeasures: hasIncompleteCommonMeasure,
    website: !resolvedDraft.isWebsiteValid,
  };

  return {
    ...issues,
    hasRequiredIssues:
      issues.name ||
      issues.quantity ||
      issues.allergens ||
      issues.preferences ||
      issues.commonMeasures,
    hasIssues: Object.values(issues).some(Boolean),
  };
}
